import React from "react";

const MINIMAL_NAV_ITEMS = [
  { key: "about", label: "About" },
  { key: "grounds", label: "Grounds" },
  { key: "facilities", label: "Facilities" },
  { key: "gallery", label: "Gallery" },
  { key: "location", label: "Visit" },
];

const MinimalTheme = ({
  venue,
  grounds = [],
  website,
  backgroundStyle = "clean",
  primaryColor = "#33A444",
  accentColor = "#111827",
  heroImage = "",
  headline,
  subheadline,
  ctaLabel = "Book Now",
  openBooking,
  sectionRenderers = {},
  themeLayout = "minimal",
  selectedThemeLayout = "minimal",
}) => {
  const bookingEnabled = website?.booking?.enabled !== false;

  const visibleNavigation = MINIMAL_NAV_ITEMS.filter(
    (item) => website?.sections?.[item.key] !== false
  );

  const orderedSections = Array.isArray(website?.sectionOrder)
    ? website.sectionOrder
        .filter((key) => key !== "hero")
        .filter((key) => website?.sections?.[key] !== false)
    : [];

  const city = venue?.location?.city || venue?.city || "Your city";
  const openingTime = venue?.policies?.openTime || "06:00";
  const closingTime = venue?.policies?.closeTime || "22:00";

  return (
    <main
      id="top"
      className={`pv-page pv-theme-${themeLayout} pv-bg-${backgroundStyle}`}
      data-theme={themeLayout}
      data-selected-theme={selectedThemeLayout}
      style={{
        "--pv-primary": primaryColor,
        "--pv-accent": accentColor,
      }}
    >
      <header className="pvmi-header">
        <nav className="pvmi-nav" aria-label="Venue navigation">
          <a className="pvmi-brand" href="#top">
            <strong>{venue?.name || "Venue"}</strong>
            <span>{city}</span>
          </a>

          <div className="pvmi-nav-links">
            {visibleNavigation.map((item) => (
              <a href={`#${item.key}`} key={item.key}>
                {item.label}
              </a>
            ))}
          </div>

          {bookingEnabled && (
            <button type="button" onClick={() => openBooking?.()}>
              {ctaLabel}
            </button>
          )}
        </nav>
      </header>

      {website?.sections?.hero !== false && (
        <section className="pvmi-hero">
          <div className="pvmi-hero-intro">
            <span className="pvmi-index">01 / Venue</span>

            <h1>{headline}</h1>

            <p>{subheadline}</p>

            <div className="pvmi-hero-actions">
              {bookingEnabled && (
                <button type="button" onClick={() => openBooking?.()}>
                  {ctaLabel}
                  <span aria-hidden="true">→</span>
                </button>
              )}

              {venue?.googleMapsUrl && (
                <a href={venue.googleMapsUrl} target="_blank" rel="noreferrer">
                  View location
                </a>
              )}
            </div>
          </div>

          <div className="pvmi-hero-media">
            {heroImage ? (
              <img src={heroImage} alt={venue?.name || "Sports venue"} />
            ) : (
              <div className="pvmi-placeholder">
                <span>{city}</span>
                <strong>{venue?.name || "Your venue"}</strong>
              </div>
            )}

            <div className="pvmi-image-meta">
              <span>{grounds.length} bookable grounds</span>
              <span>
                {openingTime} — {closingTime}
              </span>
            </div>
          </div>
        </section>
      )}

      <section className="pvmi-facts" aria-label="Venue facts">
        <div>
          <span>Grounds</span>
          <strong>{grounds.length}</strong>
        </div>

        <div>
          <span>Location</span>
          <strong>{city}</strong>
        </div>

        <div>
          <span>Opening</span>
          <strong>{openingTime}</strong>
        </div>

        <div>
          <span>Closing</span>
          <strong>{closingTime}</strong>
        </div>
      </section>

      <div className="pv-content pvmi-content">
        {orderedSections.map((key, index) => {
          const renderSection = sectionRenderers[key];

          if (!renderSection) return null;

          return (
            <div
              className="pvmi-section-wrap"
              data-section-number={String(index + 2).padStart(2, "0")}
              key={key}
            >
              {renderSection()}
            </div>
          );
        })}
      </div>

      {bookingEnabled && (
        <section className="pvmi-final-cta">
          <span>Available to book online</span>

          <div>
            <h2>Choose your ground and reserve a time.</h2>

            <button type="button" onClick={() => openBooking?.()}>
              {ctaLabel}
              <span aria-hidden="true">↗</span>
            </button>
          </div>
        </section>
      )}

      <footer className="pvmi-footer">
        <div>
          <strong>{venue?.name || "Venue"}</strong>
          <span>{city}</span>
        </div>

        <p>
          Facilities, availability and online reservations in one simple place.
        </p>

        <div className="pvmi-footer-bottom">
          <span>© {new Date().getFullYear()}</span>
          <span>Powered by OnGround</span>
        </div>
      </footer>
    </main>
  );
};

export default MinimalTheme;